<template>
  <AuthLayout
    :title="$t('authRegisterTitle')"
    :subtitle="$t('authRegisterSub')"
    show-tabs
    active-tab="register"
    @back="toMain"
    @tab-change="onTabChange"
  >
    <form class="auth-panel__form" @submit.prevent="onSubmit">
      <div class="auth-panel__steps">
        <div
          class="auth-panel__step-bar"
          :class="{ 'auth-panel__step-bar--active': step >= 1 }"
        />
        <div
          class="auth-panel__step-bar"
          :class="{ 'auth-panel__step-bar--active': step >= 2 }"
        />
      </div>
      <div class="auth-panel__step-label">
        {{ $t('authStepOf', { step }) }}
      </div>

      <template v-if="step === 1">
        <AuthInput
          v-model="name"
          :label="$t('name')"
          type="text"
          :placeholder="$t('authNamePlaceholder')"
          :errors="nameErrors"
          @update:model-value="(v) => validateNameField('name', v)"
        >
          <template #icon>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </template>
        </AuthInput>

        <AuthInput
          v-model="surname"
          :label="$t('surname')"
          type="text"
          :placeholder="$t('authSurnamePlaceholder')"
          :errors="surnameErrors"
          @update:model-value="(v) => validateNameField('surname', v)"
        />

        <AuthInput
          v-model="email"
          :label="$t('authEmailLabel')"
          type="email"
          :placeholder="$t('mail')"
          :errors="emailErrors"
        >
          <template #icon>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,12 2,6" />
            </svg>
          </template>
        </AuthInput>

        <AuthInput
          v-model="phone"
          :label="$t('phone')"
          type="tel"
          :placeholder="'+7 (700) 000-00-00'"
          :errors="phoneErrors"
          mask="+7 (###) ### ## ##"
          @change="validatePhone"
        />

        <div class="auth-panel__hint-box">
          <span>💡</span>
          <p>{{ $t('authPhoneHint') }}</p>
        </div>
      </template>

      <template v-else>
        <AuthInput
          v-model="password"
          :label="$t('authPasswordLabel')"
          type="password"
          :placeholder="$t('pass')"
          :errors="passwordErrors"
        />

        <div v-if="password" class="auth-pwd-strength">
          <div class="auth-pwd-strength__bars">
            <div
              v-for="i in 4"
              :key="i"
              class="auth-pwd-strength__bar"
              :style="{ background: i <= pwdStrength ? strengthColor : '' }"
            />
          </div>
          <div class="auth-pwd-strength__label" :style="{ color: strengthColor }">
            {{ strengthLabel }}
          </div>
        </div>

        <AuthInput
          v-model="confirmPassword"
          :label="$t('confirmPass')"
          type="password"
          :placeholder="$t('confirmPass')"
          :errors="confirmPasswordErrors"
        />

        <label class="auth-checkbox-row">
          <input v-model="agree" type="checkbox" />
          <span>
            {{ $t('agree') }}
            <a :href="DOCS?.doc_1" target="_blank" rel="noopener">{{ $t('conf2') }}</a>
            {{ $t('and') }}
            <a :href="DOCS?.doc_2" target="_blank" rel="noopener">{{ $t('conf3') }}</a>
          </span>
        </label>

        <label class="auth-checkbox-row">
          <input type="checkbox" />
          <span>{{ $t('rassylka') }}</span>
        </label>
      </template>

      <div class="auth-panel__actions">
        <button
          v-if="step === 2"
          type="button"
          class="auth-panel__back-step"
          @click="step = 1"
        >
          ←
        </button>
        <button class="auth-panel__submit" type="submit" :disabled="registerLoading" style="flex: 1">
          <span v-if="registerLoading" class="auth-spinner" />
          {{
            registerLoading
              ? '...'
              : step === 1
                ? $t('authContinue')
                : $t('authCreateAccount')
          }}
        </button>
      </div>

      <!-- Social signup — temporarily disabled
      <template v-if="step === 1">
        <div class="auth-panel__divider"><span>{{ $t('authOrRegister') }}</span></div>
        <div class="auth-panel__socials">
          <button type="button" class="auth-social-btn" disabled>Google</button>
          <button type="button" class="auth-social-btn" disabled>Telegram</button>
        </div>
      </template>
      -->

      <p class="auth-panel__footer">
        {{ $t('authHasAccount') }}
        <router-link to="/login" class="auth-panel__footer-link">{{ $t('authTabLogin') }}</router-link>
      </p>
    </form>
  </AuthLayout>
</template>

<script>
import { mask } from 'vue-the-mask';
import axios from 'axios';
import { mapActions } from 'vuex';
import { createToaster } from '@meforma/vue-toaster';
import useVuelidate from '@vuelidate/core';
import { required, minLength, helpers, email } from '@vuelidate/validators';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import AuthInput from '@/components/auth/AuthInput.vue';
import { AUTH_DOCS } from '@/constants/authDocs';

const toaster = createToaster({ position: 'top-right', duration: 2000 });

const STRENGTH_LABELS = ['', 'Слабый', 'Средний', 'Хороший', 'Надёжный'];
const STRENGTH_COLORS = ['', '#EF4444', '#F59E0B', '#10B981', '#059669'];

export default {
  name: 'RegisterGuestPage',
  components: { AuthLayout, AuthInput },
  directives: { mask },
  data() {
    return {
      v$: useVuelidate(),
      step: 1,
      name: '',
      surname: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      agree: false,
      isValidPhone: false,
      registerLoading: false,
      DOCS: { ...AUTH_DOCS },
    };
  },
  validations() {
    return {
      name: {
        required: helpers.withMessage(this.$t('req'), required),
      },
      surname: {
        required: helpers.withMessage(this.$t('req'), required),
      },
      email: {
        required: helpers.withMessage(this.$t('req'), required),
        email: helpers.withMessage(this.$t('reqMail'), email),
      },
      password: {
        required: helpers.withMessage(this.$t('req'), required),
        minLength: helpers.withMessage(this.$t('minLength'), minLength(6)),
      },
      confirmPassword: {
        required: helpers.withMessage(this.$t('req'), required),
        sameAsPassword: helpers.withMessage(this.$t('errPass'), (value) => value === this.password),
      },
      phone: {
        required: helpers.withMessage(this.$t('req'), required),
        minLength: helpers.withMessage(this.$t('req'), minLength(11)),
      },
    };
  },
  computed: {
    pwdStrength() {
      const pwd = this.password;
      if (!pwd) return 0;
      let s = 0;
      if (pwd.length >= 8) s++;
      if (/[A-Z]/.test(pwd)) s++;
      if (/[0-9]/.test(pwd)) s++;
      if (/[^A-Za-z0-9]/.test(pwd)) s++;
      return s;
    },
    strengthLabel() {
      return STRENGTH_LABELS[this.pwdStrength] || '';
    },
    strengthColor() {
      return STRENGTH_COLORS[this.pwdStrength] || '';
    },
    nameErrors() {
      return this.v$.name.$errors.map((e) => e.$message);
    },
    surnameErrors() {
      return this.v$.surname.$errors.map((e) => e.$message);
    },
    emailErrors() {
      return this.v$.email.$errors.map((e) => e.$message);
    },
    phoneErrors() {
      return this.v$.phone.$errors.map((e) => e.$message);
    },
    passwordErrors() {
      return this.v$.password.$errors.map((e) => e.$message);
    },
    confirmPasswordErrors() {
      return this.v$.confirmPassword.$errors.map((e) => e.$message);
    },
  },
  methods: {
    ...mapActions(['requestUser']),
    onTabChange(tab) {
      if (tab === 'login') {
        this.$router.push('/');
      }
    },
    validateNameField(field, value) {
      const raw = value !== undefined ? value : this[field];
      this[field] = raw.replace(/[^a-zA-Zа-яА-ЯёЁәіңғүұқөһӘІҢҒҮҰҚӨҺ]/g, '');
    },
    validatePhone() {
      let digits = this.phone.replace(/\D/g, '');
      if (digits.startsWith('8')) {
        digits = '7' + digits.slice(1);
      }
      if (digits.length !== 11 || !digits.startsWith('7')) {
        this.isValidPhone = false;
        return;
      }
      this.phone = `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
      this.isValidPhone = true;
    },
    async validateStep1() {
      this.v$.name.$touch();
      this.v$.surname.$touch();
      this.v$.email.$touch();
      this.validatePhone();
      return (
        !this.v$.name.$invalid &&
        !this.v$.surname.$invalid &&
        !this.v$.email.$invalid &&
        this.isValidPhone
      );
    },
    async onSubmit() {
      if (this.step === 1) {
        const ok = await this.validateStep1();
        if (!ok) {
          if (!this.isValidPhone) {
            toaster.error('Введите номер телефона в правильном формате.');
          }
          return;
        }
        this.step = 2;
        return;
      }

      await this.Register();
    },
    async Register() {
      await this.v$.$validate();

      if (!this.agree) {
        toaster.error(
          'Подтвердите ваше согласие с правилами пользования и политикой конфиденциальности.'
        );
        return;
      }
      if (!this.isValidPhone) {
        toaster.error('Введите номер телефона в правильном формате.');
        return;
      }

      if (!this.v$.$invalid) {
        const data = {
          name: this.name,
          surname: this.surname,
          phone: this.phone,
          email: this.email,
          password: this.password,
        };

        this.registerLoading = true;
        try {
          const res = await axios.post(`/V1/registration-guest`, data);

          if (res.status === 200) {
            localStorage.setItem('access_token', res.data.token);
            await this.requestUser();
            toaster.success(this.$t('succesSign'));
            this.$router.push('/platform');
          }
        } catch (err) {
          if (err.response?.data?.errors?.email) {
            toaster.error(err.response.data.errors.email[0]);
          } else if (err.response?.data?.errors?.phone) {
            toaster.error(err.response.data.errors.phone[0]);
          } else {
            toaster.error(this.$t('err'));
            console.error(err);
          }
        } finally {
          this.registerLoading = false;
        }
      } else {
        toaster.error(this.$t('err'));
      }
    },
    toMain() {
      this.$router.push('/');
    },
  },
};
</script>
